import { useEffect, useState } from 'react';
import { supervisorBase } from '../utils/georgeUrl';
import type { AgentData } from '../components/AgentModal';

export type AccessMap = Record<string, boolean>;

type AggregatedAccessResponse = Record<string, { authorized: boolean; available: boolean }>;

// Module-level cache — survives StrictMode remounts, busted on token change
let cachedToken: string | null = null;
let cachedPromise: Promise<AccessMap> | null = null;

export function useAccessCheck(
  accessToken: string,
  idToken: string,
  agents: AgentData[]
): AccessMap {
  const [accessMap, setAccessMap] = useState<AccessMap>({});

  useEffect(() => {
    if (!accessToken) {
      setAccessMap({});
      cachedToken = null;
      cachedPromise = null;
      return;
    }

    const liveAgents = agents.filter((a) => a.liveMetadata);
    if (!liveAgents.length) return;

    // New token → bust cache. Access + ID tokens are minted and rotated
    // together by the tokenManager, so keying on the access token alone is
    // enough to catch a refresh.
    if (accessToken !== cachedToken) {
      cachedToken = accessToken;
      cachedPromise = null;
    }

    if (!cachedPromise) {
      cachedPromise = fetch(`${supervisorBase()}/api/v1/access`, {
        // The supervisor verifies identity on the ID token — the org AS's
        // access token carries no `groups` claim to authorize against.
        headers: { Authorization: `Bearer ${accessToken}`, 'X-Id-Token': idToken },
      })
        .then((r) => {
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          return r.json() as Promise<AggregatedAccessResponse>;
        })
        .then((payload) =>
          Object.fromEntries(
            Object.entries(payload).map(([id, { authorized, available }]) => [
              id,
              authorized && available,
            ])
          )
        )
        // Network / 5xx fallback: fail-open so the user still sees the cards.
        .catch(() => Object.fromEntries(liveAgents.map((a) => [a.id, true])));
    }

    void cachedPromise.then((map) => setAccessMap(map));
  }, [accessToken, idToken, agents]);

  return accessMap;
}
